import {
  IconAlertTriangle,
  IconBan,
  IconBattery,
  IconBolt,
  IconBuilding,
  IconBuildings,
  IconBusinessplan,
  IconCalendarDollar,
  IconCash,
  IconCreditCard,
  IconEngine,
  IconEye,
  IconGift,
  IconGridDots,
  IconPlug,
  IconRefresh,
  IconSend,
  IconSettings,
  IconShield,
  IconSolarPanel,
  IconSpeakerphone,
  IconUser,
  IconUsers,

} from '@tabler/icons-vue';

import { EQUIPMENT_VIEW_ROLE } from '~/constants/equipment';

export interface RouteConfig {
  children?: RouteConfig[];
  component?: () => Promise<any>;
  external?: boolean;
  groups?: string[];
  href?: string;
  icon: any;
  label: string;
  roles?: string[];
  to?: string;
}

export const REVALIDA_CAMPAIGN_MANAGER_GROUP = 'campaign-manager';

export const routesConfig: RouteConfig[] = [
  {
    children: [
      {
        icon: IconCreditCard,
        label: 'Financiamentos',
        to: '/financings',
      },
      {
        component: () => import('~/pages/inactive-accounts.vue'),
        icon: IconCreditCard,
        label: 'Contas Inativas',
        roles: ['inactive_accounts:view'],
        to: '/inactive-accounts',
      },
    ],
    icon: IconCash,
    label: 'Financeiro',
  },
  {
    children: [
      {
        component: () => import('~/pages/inverters.vue'),
        icon: IconBolt,
        label: 'Inversores',
        roles: [EQUIPMENT_VIEW_ROLE],
        to: '/inverters',
      },
      {
        component: () => import('~/pages/microInverters.vue'),
        icon: IconPlug,
        label: 'Micro-inversores',
        roles: [EQUIPMENT_VIEW_ROLE],
        to: '/microInverters',
      },
      {
        component: () => import('~/pages/modules.vue'),
        icon: IconGridDots,
        label: 'Módulos',
        roles: [EQUIPMENT_VIEW_ROLE],
        to: '/modules',
      },
      {
        component: () => import('~/pages/batteries.vue'),
        icon: IconBattery,
        label: 'Baterias',
        roles: [EQUIPMENT_VIEW_ROLE],
        to: '/batteries',
      },
    ],
    icon: IconSolarPanel,
    label: 'Equipamentos',
  },
  {
    children: [
      {
        icon: IconUser,
        label: 'Usuários',
        to: '/users',
      },
      {
        icon: IconUsers,
        label: 'Parceiros',
        to: '/partners',
      },
    ],
    icon: IconBuilding,
    label: 'Gestão',
  },
  {
    children: [
      {
        component: () => import('~/pages/ruleRisk.vue'),
        icon: IconAlertTriangle,
        label: 'Motor de decisão',
        roles: ['engine_risk:view'],
        to: '/ruleRisk',
      },
    ],
    icon: IconShield,
    label: 'Risco',
  },
  {
    children: [
      {
        component: () => import('~/pages/ruleEngine.vue'),
        icon: IconEngine,
        label: 'Motor Pricing',
        roles: ['engine_pricing:view'],
        to: '/ruleEngine',
      },
      {
        external: true,
        href: 'https://lookerstudio.google.com/u/0/reporting/992fdd39-f992-48fd-a2b3-1a8f29675601/page/p_k2oxr8tiqd',
        icon: IconEye,
        label: 'Looker',
        roles: ['engine_pricing:view'],
      },
    ],
    icon: IconSettings,
    label: 'Sistema',
  },
  {
    children: [
      {
        icon: IconCalendarDollar,
        label: 'Cessão e Tombamento',
        roles: ['capital_markets:view'],
        to: '/cessions',
      },
      {
        icon: IconSend,
        label: 'Envio de Lastro',
        roles: ['capital_markets:view'],
        to: '/backing',
      },
      {
        icon: IconBan,
        label: 'Lista de Bloqueios',
        roles: ['capital_markets:view'],
        to: '/financing-blocklist',
      },
      {
        icon: IconSettings,
        label: 'Configurações',
        roles: ['capital_markets:view'],
        to: '/settings',
      },
    ],
    icon: IconBusinessplan,
    label: 'Mercado de Capitais',
  },
  {
    children: [
      {
        component: () => import('~/pages/revalida-campaigns.vue'),
        groups: [REVALIDA_CAMPAIGN_MANAGER_GROUP],
        icon: IconSpeakerphone,
        label: 'Campanhas',
        to: '/revalida-campaigns',
      },
      {
        component: () => import('~/pages/revalida-partner-view.vue'),
        groups: [REVALIDA_CAMPAIGN_MANAGER_GROUP],
        icon: IconBuildings,
        label: 'Visão por Parceiro',
        to: '/revalida-partner-view',
      },
      {
        component: () => import('~/pages/incentives.vue'),
        groups: [REVALIDA_CAMPAIGN_MANAGER_GROUP],
        icon: IconGift,
        label: 'Incentivos',
        to: '/incentives',
      },
    ],
    icon: IconRefresh,
    label: 'Revalida Instalação',
  },
];

const protectedOnlyRoutes: RouteConfig[] = [
  {
    component: () => import('~/pages/revalida-campaign-detail.vue'),
    groups: [REVALIDA_CAMPAIGN_MANAGER_GROUP],
    icon: IconSpeakerphone,
    label: 'Detalhe da campanha',
    to: '/revalida-campaign-detail',
  },
  {
    component: () => import('~/pages/revalida-campaign-new.vue'),
    groups: [REVALIDA_CAMPAIGN_MANAGER_GROUP],
    icon: IconSpeakerphone,
    label: 'Nova campanha',
    to: '/revalida-campaign-new',
  },
  {
    component: () => import('~/pages/revalida-partner-view.[id].vue'),
    groups: [REVALIDA_CAMPAIGN_MANAGER_GROUP],
    icon: IconBuildings,
    label: 'Detalhe do parceiro',
    to: '/revalida-partner-view/:id',
  },
  {
    component: () => import('~/pages/incentive-new.vue'),
    groups: [REVALIDA_CAMPAIGN_MANAGER_GROUP],
    icon: IconGift,
    label: 'Novo incentivo',
    to: '/incentive-new',
  },
  {
    component: () => import('~/pages/incentive-detail.vue'),
    groups: [REVALIDA_CAMPAIGN_MANAGER_GROUP],
    icon: IconGift,
    label: 'Detalhe do incentivo',
    to: '/incentive-detail',
  },
];

const flattenRoutes = (routes: RouteConfig[]): RouteConfig[] => routes.flatMap((route) => (
  route.children ? [route, ...flattenRoutes(route.children)] : [route]
));

export const getProtectedRoutes = () => [...flattenRoutes(routesConfig), ...protectedOnlyRoutes]
  .filter((route) => (route.roles || route.groups) && route.to && route.component)
  .reduce((acc, route) => {
    if (route.to && route.component) {
      acc[route.to] = {
        component: route.component,
        requiresGroup: route.groups,
        requiresRole: route.roles,
      };
    }
    return acc;
  }, {} as Record<string, {
    component: () => Promise<any>;
    requiresGroup?: string[];
    requiresRole?: string[];
  }>);

function hasRouteAccess(
  route: RouteConfig,
  userHasRole?: (_roles: string[]) => boolean,
  userHasGroup?: (_groups: string[]) => boolean,
): boolean {
  const hasRoleAccess = !route.roles || (userHasRole?.(route.roles) ?? false);
  const hasGroupAccess = !route.groups || (userHasGroup?.(route.groups) ?? false);
  return hasRoleAccess && hasGroupAccess;
}

export const getNavItems = (
  userHasRole?: (_roles: string[]) => boolean,
  userHasGroup?: (_groups: string[]) => boolean,
): RouteConfig[] => {
  const filterRoute = (route: RouteConfig): null | RouteConfig => {
    if (route.children) {
      const filteredChildren = route.children
        .map(filterRoute)
        .filter(Boolean) as RouteConfig[];

      if (filteredChildren.length > 0) {
        return { ...route, children: filteredChildren };
      }
      return null;
    }

    if (!route.roles && !route.groups) return route;
    return hasRouteAccess(route, userHasRole, userHasGroup) ? route : null;
  };

  return routesConfig
    .map(filterRoute)
    .filter(Boolean) as RouteConfig[];
};
